import React from "react";
import { NavLink, Outlet, useNavigate, Navigate, useLocation } from "react-router-dom";
import { LayoutDashboard, PlusCircle, ArrowLeftRight, ReceiptText, LogOut, Bell, Coins, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/common";
import NotificationBell from "@/components/NotificationBell";
import SupportWidget from "@/components/SupportWidget";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", label: "Dashboard", short: "Home", Icon: LayoutDashboard, id: "dashboard" },
  { to: "/trade", label: "Trade", short: "Trade", Icon: Coins, id: "trade" },
  { to: "/deposit", label: "Deposit", short: "Deposit", Icon: PlusCircle, id: "deposit" },
  { to: "/exchange", label: "Exchange", short: "Exchange", Icon: ArrowLeftRight, id: "exchange" },
  { to: "/transactions", label: "History", short: "History", Icon: ReceiptText, id: "transactions" },
];

export default function UserShell() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  if (!ready) return <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;

  return (
    <div className="relative min-h-screen bg-background pb-20 md:pb-0">
      {/* Subtle floating ambient background on all authenticated pages */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div
          className="absolute -top-24 left-1/4 h-80 w-80 rounded-full opacity-40 float-y slow"
          style={{ background: "radial-gradient(circle, hsl(44 86% 60% / 0.14) 0%, transparent 65%)" }}
        />
        <div
          className="absolute right-10 top-40 h-72 w-72 rounded-full opacity-40 float-y"
          style={{ background: "radial-gradient(circle, hsl(186 72% 48% / 0.14) 0%, transparent 65%)" }}
        />
        <div
          className="absolute -bottom-10 left-1/3 h-96 w-96 rounded-full opacity-30 float-y fast"
          style={{ background: "radial-gradient(circle, hsl(36 72% 40% / 0.12) 0%, transparent 65%)" }}
        />
      </div>
      <header className="sticky top-0 z-40 border-b bg-white">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-6 px-4 sm:px-6 lg:px-8">
          <NavLink to="/dashboard" data-testid="header-logo-link"><Logo /></NavLink>
          <nav className="hidden items-center gap-1 md:flex">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.id}-link`}
                className={({ isActive }) => cn("rounded-lg px-3 py-2 text-sm font-medium transition-colors", isActive ? "bg-secondary text-primary" : "text-muted-foreground hover:text-foreground")}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-1">
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu-button">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                    {(user.name || user.email)[0].toUpperCase()}
                  </span>
                  <span className="hidden max-w-[140px] truncate text-sm sm:inline">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="text-sm">{user.name}</p>
                  <p className="truncate text-xs font-normal text-muted-foreground">{user.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/referrals")} data-testid="user-menu-referrals"><Users className="mr-2 h-4 w-4" /> Refer & earn</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/notifications")} data-testid="user-menu-notifications"><Bell className="mr-2 h-4 w-4" /> Notifications</DropdownMenuItem>
                <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="user-menu-logout"><LogOut className="mr-2 h-4 w-4" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
        <Outlet />
      </main>
      <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t bg-white md:hidden" data-testid="mobile-bottom-nav">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`mobile-nav-${n.id}-link`}
            className={({ isActive }) => cn("flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium", isActive ? "text-primary" : "text-muted-foreground")}
          >
            <n.Icon className="h-5 w-5" />
            {n.short}
          </NavLink>
        ))}
      </nav>
      <SupportWidget />
    </div>
  );
}
